/**
 * Mitteilung „Vespera hat geantwortet“ nach einem Wach-Wort-Satz bei verdeckter App. (14.10.2026, Build 110)
 *
 * Der Satz nach „Hey Vespera“ geht auch bei verdeckter App ab (wachwort-satz.ts, wachwortImHintergrund). Kommt die Antwort,
 * soll das iPhone eine lokale Mitteilung zeigen. Die App (PawbotVoicePlugin.wachwortAntwort) zeigt sie nur hinten und nur kurz
 * nach einem Treffer; hier wird entschieden, WAS hineinkommt.
 *
 * ⚠️ Datenschutz Sperrbildschirm: Standard ist der neutrale Text „Antwort bereit“ OHNE Antworttext. Die Antwort selbst
 *    verrät nicht verlässlich, ob sie Privates enthält — darum entscheidet allein die Einstellung
 *    `antwortVorschauSperrbildschirm` (Standard aus) über eine Vorschau.
 */
import { wachwortAntwort } from "./native-voice.js";

/** So viele Zeichen trägt die Vorschau höchstens (die App kürzt ebenfalls auf 80). */
export const VORSCHAU_MAX = 80;

/** Der neutrale Text ohne Antwort. */
export const ANTWORT_NEUTRAL = "Antwort bereit";

/** Emoji, Symbole und Verbindungszeichen (Hautton, Variation, Zero-Width-Joiner). */
const EMOJI = /[\p{Extended_Pictographic}\p{Emoji_Modifier}\u200d\ufe0e\ufe0f\u20e3]/gu;

/** Auszeichnung entfernen: Codeblöcke, Links, Bilder, Überschriften (ganze Zeile), Listen, Zitate, Fett/Kursiv/Code. */
function ohneAuszeichnung(t: string): string {
  return t
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s.*$/gm, " ")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, "")
    .replace(/[*_~`]+/g, "");
}

/**
 * Der erste Satz der Antwort, ohne Auszeichnung und Emoji, höchstens `VORSCHAU_MAX` Zeichen (mit „…“ bei Kürzung).
 * Leer, wenn nichts Lesbares bleibt. Rein.
 */
export function antwortVorschau(text: string): string {
  const klar = ohneAuszeichnung(String(text ?? ""))
    .replace(EMOJI, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!klar) return "";
  const satz = /^.*?[.!?](?=\s|$)/u.exec(klar)?.[0] ?? klar;
  const zeichen = Array.from(satz.trim());
  if (zeichen.length <= VORSCHAU_MAX) return zeichen.join("");
  return zeichen.slice(0, VORSCHAU_MAX - 1).join("").trimEnd() + "…";
}

/** Was in die Mitteilung kommt: die Vorschau nur bei eingeschalteter Einstellung, sonst neutral. Rein. */
export function mitteilungsText(antwort: string, vorschauAn: boolean): string {
  if (!vorschauAn) return ANTWORT_NEUTRAL;
  return antwortVorschau(antwort) || ANTWORT_NEUTRAL;
}

export interface AntwortLage {
  /** Ist die Seite sichtbar? Nur bei verdeckter Seite gibt es eine Mitteilung. */
  sichtbar: boolean;
  /** Einstellung „Antwortvorschau auf dem Sperrbildschirm“. */
  vorschauAn: boolean;
}

/**
 * Meldet der App die fertige Antwort eines Wach-Wort-Satzes. Wirft nie. `true`, wenn die App den Aufruf angenommen hat.
 * Ohne Plugin, in einem älteren Build oder bei sichtbarer Seite passiert nichts.
 */
export async function wachwortAntwortMelden(
  antwort: string,
  lage: AntwortLage,
  senden: (text: string) => Promise<boolean> = wachwortAntwort,
): Promise<boolean> {
  if (lage.sichtbar) return false;
  try {
    return await senden(mitteilungsText(antwort, lage.vorschauAn));
  } catch {
    return false;
  }
}
